<script setup>
import {
  LogOut,
  Bell,
  Search,
  ChevronRight,
  ChevronsRight,
  AlertTriangle,
  SlidersVertical,
  ChevronDown,
} from "lucide-vue-next";
import { ClockIcon, HomeIcon, ViewListIcon } from "@heroicons/vue/outline";
import {
  ref,
  onMounted,
  onBeforeUnmount,
  reactive,
  computed,
  watchEffect,
} from "vue";
import axios from "axios";
import { usePage } from "@inertiajs/inertia-vue3";
import BreezeNavLink from "@/Components/NavLink.vue";
import Logo from "@/Components/Logo.vue";
import { Link } from "@inertiajs/inertia-vue3";
import NetworkStatut from "./Tablette/NetworkStatut.vue";
import SearchBar from "./SearchBar.vue";
import { useToast } from "vue-toastification";
import { VueSidePanel } from "vue3-side-panel";
import ScrollToTop from "./Tools/ScrollToTop.vue";
import { UserCheck } from "lucide-vue-next";
import { onClickOutside } from "@vueuse/core";
import LoaderBar from "./LoaderBar.vue";
import { Icon } from "@iconify/vue";
import { Inertia } from "@inertiajs/inertia";
import Loader from "./Loader.vue";
import useFolders from "@/Services/FoldersServices";
import ThemeClass from "@/Components/ThemeClass.vue";
import ReportingBugs from "@/Components/ReportingBugs.vue";
import HasRoleOrPermissions from "./Settings/Users/HasRoleOrPermissions.vue";

const { searchFolders, searchFoldersByParam } = useFolders();

const dropdownMenu = ref();
const toggleDarkMode = () => {
  document.documentElement.classList.toggle("dd-nav-dark");
};

// const { user, fetchUser } = useUser()
const planningBoards = ref([]);
const navigation = reactive([]);
const load = reactive({ user: true });
const open = reactive({ userMenu: false, menuMobile: false });
const scrollY = ref(0);
const user = ref(usePage().props.value.auth.user);
const toast = useToast();
const props = defineProps(["title", "tabletteMode"]);
const menuSelected = ref("");
const subMenuSelected = ref("");
const clickOnMenu = ref(null);
const stickyDiv = ref(null);
const openSearch = ref(false);
const query = ref("");
const loadingFolders = ref(false);
const searchBarRef = ref(null);

const toggleMenu = () => {
  open.menuMobile = !open.menuMobile;
};

const models = reactive({
  stocks: ["create permissions", "delete permissions", "admin"],
  planning: ["create planning", "delete planning", "admin"],
  cartes: ["create stocks", "delete stocks", "admin"],
  reglages: ["create stocks", "delete stocks", "admin"],
  stat: ["create stocks", "delete stocks", "admin"],
  production: ["create stocks", "delete stocks", "admin"],
  notifications: ["create notifications", "admin"],
});
const navigationMenus = ref([]);
const isHeight = reactive({ reduced: false });
let lastScrollTop = 0;
const scrollThreshold = 100; // Seuil de 100px
const scrollTolerance = 10; // Tolérance de 10px
const nbrsOfNotifications = ref(0);

const getMenus = async () => {
  try {
    load.userMenu = true;
    let response = await axios.get("/reglages/menus");
    navigationMenus.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    load.userMenu = false;
  }
};

const getNumberOfNotifications = async () => {
  try {
    let response = await axios.get(
      "/api/notifications/get_number_of_notifications"
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

onMounted(async () => {
  if (!props.tabletteMode) {
    getMenus();
  }
  nbrsOfNotifications.value = await getNumberOfNotifications();
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  // Vérification du seuil avec une tolérance
  if (scrollTop > scrollThreshold + scrollTolerance && !isHeight.reduced) {
    isHeight.reduced = true; // Réduire la hauteur si on dépasse le seuil + tolérance
  } else if (
    scrollTop < scrollThreshold - scrollTolerance &&
    isHeight.reduced
  ) {
    isHeight.reduced = false; // Remettre la hauteur initiale si on redescend sous le seuil - tolérance
  }

  lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // Éviter les valeurs négatives
};

const toggleMenuUser = () => {
  open.userMenu = !open.userMenu;
};

const menuUser = ref();
onClickOutside(menuUser, () => {
  open.userMenu = false;
});

const selectedMenuRef = ref();
onClickOutside(selectedMenuRef, () => {
  menuSelected.value = null;
});

const selectedSubMenuRef = ref(null);
onClickOutside(selectedSubMenuRef, () => {
  subMenuSelected.value = null;
});

onClickOutside(searchBarRef, () => {
  query.value = "";
});

const redirect = (item) => {
  clickOnMenu.value = item.id;
  window.document.location.href = item.url;
};

// const toggleSelectedMenu = (id) => {
//    if (menuSelected.value === id) menuSelected.value = null
//    else menuSelected.value = id
// }

// const toggleSelectedSubMenu = (id) => {
//    if (subMenuSelected.value === id) subMenuSelected.value = null
//    else subMenuSelected.value = id
// }

const debounce = (func, delay) => {
  let timerId;
  return (...args) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const filteredFolders = debounce(async () => {
  if (query.value.length >= 3) {
    searchFolders.value = [];

    loadingFolders.value = true;
    try {
      await searchFoldersByParam({
        query: query.value,
        filterType: {
          folderNumber: true,
          CM: true,
          cuisiniste: false,
          cityOrCP: false,
          deletedFolders: false,
          matter: false,
        },
      });

      loadingFolders.value = false;
    } catch (error) {
      console.log(error);
    } finally {
      loadingFolders.value = false;
    }
  }
}, 1000);

const openWindow = (folder_id) => {
  window.open(`/planning/folders/${folder_id}`);
  query.value = "";
};
</script>

<template>
  <div
    :class="[
      props.tabletteMode ? '' : 'sticky top-0',
      'flex items-center w-full h-full z-50 ',
    ]"
  >
    <SearchBar
      :openSearch="openSearch"
      :query="query"
      @update:openSearch="openSearch = $event"
    />
    <div class="w-full">
      <div ref="stickyDiv">
        <!-- BEGIN: Logo -->
        <div
          :class="[
            'mx-auto flex items-center justify-between transition-all duration-500 shadow-xl mt-1',
            'bg-primary/90 backdrop-blur-md rounded-2xl px-4 sm:px-5',
            isHeight.reduced ? 'h-14' : 'h-20',
            'w-[calc(100%-1rem)] sm:w-[98%]',
          ]"
        >
          <template v-if="props.tabletteMode">
            <Logo :tabletteMode="props.tabletteMode" />
          </template>
          <template v-if="!props.tabletteMode">
            <!-- MENU MOBILE -->
            <div class="flex items-center h-full 2xl:hidden">
              <button
                class="flex flex-col items-center justify-center hover:bg-secondary rounded-xl"
                @click="toggleMenu()"
              >
                <Icon
                  icon="solar:hamburger-menu-outline"
                  width="48"
                  height="48"
                />
              </button>
            </div>
            <!-- <template v-if="load.userMenu">
              <LoaderBar />
            </template> -->
            <!-- MENU DESKTOP -->
            <div class="items-center justify-start flex-1 hidden 2xl:flex">
              <div>
                <Logo
                  :tabletteMode="props.tabletteMode"
                  :scroll="isHeight.reduced"
                />
              </div>
              <div class="flex items-center" ref="selectedMenuRef">
                <div v-for="item in navigationMenus" :key="item.id">
                  <a
                    :href="item.newChildren.length == 0 ? item.url : '#'"
                    class="relative"
                    v-if="item.is_visible"
                    @click="
                      item.newChildren.length == 0
                        ? redirect(item)
                        : (menuSelected = item.id)
                    "
                  >
                    <div
                      :class="[
                        'flex items-center space-x-2 mx-2 transform duration-300 py-1 px-2 rounded-lg cursor-pointer hover:bg-primary',
                        menuSelected == item.id ? 'bg-primary' : '',
                      ]"
                    >
                      <Icon :icon="item.icon" />
                      <span class="font-semibold font-sulphurPoint">{{
                        item.label
                      }}</span>
                      <Icon
                        icon="solar:alt-arrow-down-linear"
                        v-if="item.newChildren.length > 0"
                        :class="[
                          'transform duration-300',
                          menuSelected == item.id ? '' : '-rotate-180',
                        ]"
                      />
                    </div>
                    <transition name="nested">
                      <template
                        v-if="
                          item.newChildren.length > 0 &&
                          menuSelected === item.id
                        "
                      >
                        <div
                          :class="[
                            isHeight.reduced ? 'pt-3' : 'pt-6',
                            'absolute left-0 top-9 transform duration-500',
                          ]"
                        >
                          <div
                            :class="[
                              'p-2 bg-primary rounded-xl shadow-xl z-20 grid ',
                              item.newChildren.length > 4
                                ? 'grid-cols-2 w-[750px]'
                                : 'grid-cols-1 w-[375px]',
                            ]"
                          >
                            <template v-for="subItem in item.newChildren">
                              <template v-if="subItem.is_visible">
                                <a
                                  :href="
                                    subItem.newChildren.length === 0
                                      ? subItem.url
                                      : '#'
                                  "
                                  @click="
                                    subItem.newChildren.length === 0
                                      ? redirect(subItem)
                                      : (subMenuSelected = subItem.id)
                                  "
                                  :class="[
                                    'flex items-center justify-between mx-2 py-1 px-2 hover:bg-secondary rounded-lg cursor-pointer relative',
                                    subMenuSelected === subItem.id
                                      ? 'bg-primary'
                                      : '',
                                  ]"
                                  :key="subItem.id"
                                >
                                  <div class="flex items-center space-x-1">
                                    <Icon :icon="subItem.icon" />
                                    <p class="p-1 font-sulphurPoint">
                                      {{ subItem.label }}
                                    </p>
                                    <Loader
                                      size="4"
                                      v-if="clickOnMenu === subItem.id"
                                    />
                                  </div>
                                  <Icon
                                    icon="solar:alt-arrow-right-linear"
                                    v-if="subItem.newChildren.length > 0"
                                  />
                                  <transition name="nested-sub-menu">
                                    <template
                                      v-if="
                                        subItem.newChildren.length > 0 &&
                                        subMenuSelected === subItem.id
                                      "
                                    >
                                      <div
                                        ref="selectedSubMenuRef"
                                        class="absolute top-0 z-20 p-2 shadow-xl left-64 bg-primary rounded-xl w-96"
                                      >
                                        <template
                                          v-for="subSubItem in subItem.newChildren"
                                        >
                                          <a
                                            :href="subSubItem.url"
                                            @click="redirect(subSubItem)"
                                            v-if="subSubItem.is_visible"
                                            class="relative flex items-center px-2 py-1 mx-2 space-x-1 rounded-lg hover:bg-primary"
                                            :key="subSubItem.id"
                                          >
                                            <Icon :icon="subSubItem.icon" />
                                            <p class="p-1 font-sulphurPoint">
                                              {{ subSubItem.label }}
                                            </p>
                                            <Loader
                                              size="4"
                                              v-if="
                                                clickOnMenu === subSubItem.id
                                              "
                                            />
                                          </a>
                                        </template>
                                      </div>
                                    </template>
                                  </transition>
                                </a>
                              </template>
                            </template>
                          </div>
                        </div>
                      </template>
                    </transition>
                  </a>
                </div>
              </div>
            </div>
          </template>
          <!-- BEGIN: Search -->
          <div
            ref="searchBarRef"
            class="relative flex items-center space-x-2 mr-4 flex-1 md:flex-none"
            v-if="!props.tabletteMode"
          >
            <div class="relative">
              <input
                type="search"
                v-model="query"
                class="input-modern w-full max-w-[200px] sm:max-w-none sm:w-64 lg:w-96"
                placeholder="Recherche rapide"
                @keyup="query.length >= 3 && filteredFolders()"
              />
              <button
                class="absolute flex items-center justify-center -translate-y-1/2 right-4 top-1/2"
                @click="openSearch = !openSearch"
              >
                <div class="">
                  <SlidersVertical size="16" />
                </div>
              </button>
            </div>
            <div
              v-if="query !== ''"
              class="absolute left-0 right-0 z-10 rounded-xl shadow top-12 bg-primary"
            >
              <div class="flex items-center px-2 py-3 text-xs">
                <div
                  v-if="searchFolders?.length == 0 && !loadingFolders"
                  class="flex items-center space-x-1"
                >
                  <AlertTriangle size="16" />
                  <p class="ml-2">Aucun résultat pour "{{ query }}"</p>
                </div>
                <div v-else class="flex items-center space-x-1">
                  <Loader size="4" />
                  <p class="ml-2">Recherche en cours...</p>
                </div>
              </div>
            </div>
            <div
              v-if="query.length > 1"
              id="querySearch"
              class="rounded-xl absolute top-12 right-0 left-0 z-10 bg-primary shadow max-h-[90vh] overflow-auto"
            >
              <template v-for="folder in searchFolders" :key="folder.id">
                <div
                  @click="openWindow(folder.id)"
                  class="w-full px-2 py-3 text-xs font-semibold cursor-pointer hover:bg-primary"
                >
                  <div class="flex flex-wrap justify-start">
                    <div class="mr-2 bg-secondary px-1 rounded">
                      {{ folder.ref }}
                    </div>
                    <div>{{ folder.ref_client }}</div>
                  </div>
                  <div class="flex flex-wrap justify-start text-xxs">
                    <p class="mr-2">{{ folder.society.name ?? "" }}</p>
                    <p>{{ folder.client ? folder.client.full_name : "" }}</p>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <!-- END: Search -->
          <div class="flex items-center space-x-4">
            <!-- BEGIN: User Menu -->

            <div class="relative flex items-center space-x-2">
              <button
                @click="toggleMenuUser"
                class="flex items-center h-14 input-modern space-x-4"
              >
                <div
                  class="w-8 h-8 rounded-xl bg-cover bg-center border border-text-secondary/20"
                  :style="`background-image: url(${user.photo})`"
                />
                <div class="absolute -top-1 right-0">
                  <div
                    class="flex items-center justify-center w-4 h-4 bg-red-600 rounded-full"
                  >
                    <p class="text-xxs text-white font-sulphurPoint">
                      {{ nbrsOfNotifications }}
                    </p>
                  </div>
                </div>
                <ChevronDown size="14" class="" />
              </button>
              <transition name="nested">
                <div
                  ref="menuUser"
                  class="absolute right-0 w-60 text-sm shadow-lg bg-secondary border border-secondary rounded-xl top-16 overflow-hidden z-9999"
                  v-if="open.userMenu"
                >
                  <div>
                    <a
                      :href="route('dashboard')"
                      class="flex items-center p-1 space-x-3 duration-150 transform group hover:bg-secondary"
                    >
                      <div
                        class="p-2 duration-150 transform bg-primary rounded-xl"
                      >
                        <UserCheck size="16" class="duration-150 transform" />
                      </div>
                      <div class="flex flex-col">
                        <span class="font-medium uppercase font-sulphurPoint"
                          >Mon dashboard</span
                        >
                        <span class="text-xs text-primaryT">{{
                          user.full_name
                        }}</span>
                      </div>
                    </a>
                    <ThemeClass />

                    <a
                      :href="route('my-notifications')"
                      class="flex items-center p-1 space-x-3 duration-150 transform group hover:bg-secondary"
                    >
                      <div
                        class="p-2 duration-150 transform bg-primary rounded-xl"
                      >
                        <Icon
                          icon="solar:notification-unread-broken"
                          size="16"
                          class="duration-150 transform"
                        />
                      </div>
                      <div class="flex flex-col">
                        <span class="font-medium uppercase font-sulphurPoint"
                          >Mes notifications</span
                        >
                        <span class="text-xs text-primaryT">
                          {{ nbrsOfNotifications }} non lue(s)
                        </span>
                      </div>
                    </a>
                    <HasRoleOrPermissions v-bind:models="models.notifications">
                      <template #default="{ can }">
                        <a
                          v-if="can"
                          href="/creer_une_notification"
                          class="flex items-center p-1 space-x-3 duration-150 transform group hover:bg-secondary"
                        >
                          <div
                            class="p-2 duration-150 transform bg-primary rounded-xl"
                          >
                            <Icon
                              icon="solar:mailbox-broken"
                              size="16"
                              class="duration-150 transform"
                            />
                          </div>
                          <div class="flex flex-col">
                            <span
                              class="font-medium uppercase font-sulphurPoint"
                              >Envoyer une notification</span
                            >
                          </div>
                        </a>
                      </template>
                    </HasRoleOrPermissions>
                    <BreezeNavLink
                      :href="route('logout')"
                      method="post"
                      as="button"
                      class="flex items-center w-full p-1 space-x-3 duration-150 transform group hover:bg-secondary"
                    >
                      <div
                        class="p-2 duration-150 transform bg-primary rounded-xl"
                      >
                        <LogOut
                          size="16"
                          class="duration-150 transform bg-primary"
                        />
                      </div>
                      <span class="uppercase font-sulphurPoint"
                        >Déconnexion</span
                      >
                    </BreezeNavLink>
                  </div>
                </div>
              </transition>
            </div>

            <!-- END: Account Menu -->
          </div>
        </div>
        <VueSidePanel
          v-model="open.menuMobile"
          side="left"
          width="min(90%, 400px)"
          z-index="9999"
        >
          <div class="p-12 bg-primary text-primaryT">
            <div>
              <div v-for="item in navigationMenus" :key="item.id">
                <template
                  v-if="item.newChildren && item.newChildren.length > 0"
                >
                  <Link
                    class="block px-3 text-2xl duration-300 transform rounded hover:bg-secondary"
                    :href="item.url"
                  >
                    {{ item.label }}</Link
                  >
                  <template
                    v-for="subItem in item.newChildren"
                    :key="subItem.id"
                  >
                    <div class="flex flex-col pl-6">
                      <Link :href="subItem.url" class="block px-3 text-lg">
                        <div
                          class="flex items-center px-3 space-x-3 duration-300 transform rounded hover:bg-secondary"
                          v-if="subItem.is_visible"
                        >
                          <ChevronRight size="12" />
                          <span>{{ subItem.label }}</span>
                        </div>
                        <Link
                          :href="subSubItem.url"
                          class="block px-3 text-lg"
                          v-for="subSubItem in subItem.newChildren"
                          :key="subSubItem.id"
                        >
                          <template v-if="subItem.newChildren">
                            <div
                              class="flex items-center px-3 space-x-3 duration-300 transform rounded hover:bg-secondary"
                              v-if="subSubItem.is_visible"
                            >
                              <ChevronsRight size="12" />
                              <span>{{ subSubItem.label }}</span>
                            </div>
                          </template>
                        </Link>
                      </Link>
                    </div>
                  </template>
                </template>
                <template v-else>
                  <Link
                    class="block px-3 text-2xl duration-300 transform rounded hover:bg-secondary"
                    :href="item.url"
                    v-if="item.is_visible"
                    >{{ item.label }}</Link
                  >
                </template>
              </div>
            </div>
          </div>
        </VueSidePanel>
      </div>
      <div v-if="!props.tabletteMode" class="fixed bottom-6 left-6 z-9999">
        <reporting-bugs />
      </div>
      <div class="fixed top-5 left-3">
        <NetworkStatut />
      </div>
      <ScrollToTop />

      <!-- END: Top Bar -->
    </div>
  </div>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: all 0.15s ease-in-out;
}

.nested-enter-from,
.nested-leave-to {
  transform: translateY(-100px);
  height: 0;
  opacity: 0;
}

.nested-sub-menu-enter-active,
.nested-sub-menu-leave-active {
  transition: all 0.15s ease-in-out;
}

.nested-sub-menu-enter-from,
.nested-sub-menu-leave-to {
  transform: translateX(-100px);
  opacity: 0;
}
</style>
